/**
 * N2ResearchPage — the letter archive.
 *
 * Reads live data from getNotchesLetters (public GET on bb-api). Free tier
 * shows metadata + Read link; paid tier shows metadata + Locked chip (paid
 * full-body reader lands in Slice 3). Empty state is honest: "no letters
 * published yet."
 *
 * Subscription structure follows brand/naming.md: Notches Weekly (free),
 * Notches Research ($99-199/mo). Pricing shown here is directional (see
 * NOTCHES_REBRAND_PLAN.md §10 Q3).
 */

import React, { useEffect, useState } from 'react';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import {
  Eyebrow, PageTitle, PageLede, SectionLabel, SectionHead,
  Body, PrimaryButton, SecondaryButton,
} from './N2Primitives';
import { useAuth } from '../contexts/AuthContext';
import { weeklyReportsService, WeeklyReportSummary } from '../services/weeklyReportsService';

export const N2ResearchPage: React.FC = () => (
  <N2Frame page="research">
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 32px 40px' }}>
      <Eyebrow>Research</Eyebrow>
      <PageTitle italic="delivered weekly.">
        The research letter,
      </PageTitle>
      <PageLede>
        Every Friday, one letter. The most notable readings from the week,
        the regime they fired in, and what changed since the last letter.
        Free tier included; full archive by subscription.
      </PageLede>

      <SubscribeCallout />
      <IssueArchive />
      <PricingBreakdown />
    </div>
  </N2Frame>
);

// ---- Subscribe callout ----------------------------------------------------

const SubscribeCallout: React.FC = () => {
  const { token } = useAuth();
  // Signed-in readers already have an account -- hide the sign-up pitch.
  if (token) return null;
  return (
    <div style={{
      marginTop: 48, padding: '32px 36px',
      background: palette.paper2, border: `1px solid ${palette.line}`,
      borderRadius: 4, position: 'relative',
      display: 'grid', gridTemplateColumns: '1.5fr auto', gap: 32,
      alignItems: 'center',
    }} className="n2-subscribe">
      <div style={{
        position: 'absolute', top: 0, left: 36, width: 40, height: 3,
        background: palette.gold,
      }} />
      <div>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: palette.goldDeep, marginBottom: 8,
        }}>Notches Weekly · free</div>
        <div style={{
          fontFamily: 'var(--serif)', fontSize: 24, lineHeight: 1.25,
          color: palette.ink, marginBottom: 6,
        }}>One letter, every Friday.</div>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
        }}>Free forever. No card. Create an account so we can verify your email
          before we start sending — SES requires it.</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
        <PrimaryButton to="/register?source=notches-weekly">Create your free account</PrimaryButton>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
        }}>Already have one? <a href="/login" style={{
          color: palette.goldDeep, textDecoration: 'none',
          borderBottom: `1px solid ${palette.gold}60`,
        }}>Log in</a></div>
      </div>
    </div>
  );
};

// ---- Weekly archive (live from weeklyReportsService) ---------------------

const IssueArchive: React.FC = () => {
  const { token } = useAuth();
  const [weeks, setWeeks] = useState<WeeklyReportSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true); setError(null);
    weeklyReportsService.list(token)
      .then((res) => {
        if (cancelled) return;
        // Prefer notches; fall back to bb for BB-only members. Sorted newest-first.
        const list = res.notches?.length ? res.notches : (res.bb || []);
        setWeeks(list);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const countLabel = !token
    ? 'sign in to read'
    : loading
      ? 'loading…'
      : error
        ? 'unavailable'
        : `${weeks?.length ?? 0} letters`;

  return (
    <section style={{ marginTop: 64 }}>
      <SectionLabel left="Archive" right={countLabel} />
      <div style={{ marginTop: 24 }}>
        {!token && <ArchiveSignInGate />}
        {token && loading && <ArchiveLoading />}
        {token && !loading && error && <ArchiveError message={error} />}
        {token && !loading && !error && (weeks?.length ?? 0) === 0 && <ArchiveEmpty />}
        {token && !loading && !error && (weeks?.length ?? 0) > 0 &&
          weeks!.map((w) => <WeekRow key={w.weekIso} week={w} />)}
      </div>
    </section>
  );
};

const ArchiveSignInGate: React.FC = () => (
  <div style={{
    padding: '40px 40px', border: `1px dashed ${palette.line}`,
    background: palette.paper, borderRadius: 4,
    display: 'grid', gridTemplateColumns: '1fr auto', gap: 32, alignItems: 'center',
  }}>
    <div>
      <div style={{
        fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22,
        color: palette.ink2, marginBottom: 8,
        textWrap: 'balance' as React.CSSProperties['textWrap'],
      }}>
        Notches Weekly is free, but delivery requires a verified account.
      </div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 14, color: palette.ink3,
        lineHeight: 1.6, maxWidth: '58ch',
      }}>
        Sign in to read the archive. Or subscribe above — takes a minute, and
        every future letter lands here and in your inbox.
      </div>
    </div>
    <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
      <PrimaryButton to="/register?source=notches-weekly">Subscribe free</PrimaryButton>
      <SecondaryButton to="/login?next=/n2/research">Sign in</SecondaryButton>
    </div>
  </div>
);

const ArchiveLoading: React.FC = () => (
  <div style={{
    padding: '32px 0', fontFamily: 'var(--sans)', fontSize: 14,
    color: palette.ink3,
  }}>Loading the archive…</div>
);

const ArchiveError: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    padding: '24px 28px', border: `1px solid ${palette.negative}40`,
    background: `${palette.negative}10`, borderRadius: 3,
    fontFamily: 'var(--sans)', fontSize: 13, color: palette.negative,
  }}>
    Archive is temporarily unavailable. Try again in a minute.
    <div style={{ marginTop: 6, color: palette.ink3, fontSize: 11 }}>{message}</div>
  </div>
);

const ArchiveEmpty: React.FC = () => (
  <div style={{
    padding: '48px 40px', border: `1px dashed ${palette.line}`,
    background: palette.paper, borderRadius: 4, textAlign: 'center',
  }}>
    <div style={{
      fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22,
      color: palette.ink2, marginBottom: 12,
      textWrap: 'balance' as React.CSSProperties['textWrap'],
    }}>
      No letters available for your subscription yet.
    </div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 14, color: palette.ink3,
      maxWidth: '48ch', margin: '0 auto', lineHeight: 1.6,
    }}>
      The Sunday letter lands here as it publishes. If you think you should
      be seeing something, check your entitlements or contact support.
    </div>
  </div>
);

function fmtGenerated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
}

const WeekRow: React.FC<{ week: WeeklyReportSummary }> = ({ week }) => (
  <article style={{
    display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: 32,
    padding: '28px 0', borderTop: `1px solid ${palette.line}`,
    alignItems: 'baseline',
  }} className="n2-issue-row">
    <div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.ink3, marginBottom: 6,
      }}>{week.weekIso}</div>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3,
        letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums',
      }}>sent {fmtGenerated(week.generatedAt)}</div>
    </div>
    <div>
      <h3 style={{
        fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22,
        lineHeight: 1.25, margin: '0 0 8px', color: palette.ink,
        letterSpacing: '-0.005em',
      }}>Week of {week.weekLabel}</h3>
      <p style={{
        fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.6,
        color: palette.ink2, margin: 0, maxWidth: '60ch',
      }}>Sunday recap and week-ahead read — SPY, VIX, regime shifts, CFTC positioning.</p>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <TierPill tone="positive" label="free" />
      <a href={`/n2/research/${week.weekIso}`} style={{
        fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
        color: palette.ink, textDecoration: 'none',
        borderBottom: `1.5px solid ${palette.gold}`, paddingBottom: 1,
      }}>Read →</a>
    </div>
  </article>
);

const TierPill: React.FC<{ tone: 'positive' | 'gold'; label: string }> = ({ tone, label }) => {
  const color = tone === 'positive' ? palette.positive : palette.goldDeep;
  const bgAlpha = tone === 'positive' ? '12' : '20';
  const bg = tone === 'positive' ? `${palette.positive}${bgAlpha}` : `${palette.gold}${bgAlpha}`;
  return (
    <span style={{
      fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      color, padding: '3px 10px',
      border: `1px solid ${color}40`, background: bg, borderRadius: 2,
    }}>{label}</span>
  );
};

// ---- Pricing --------------------------------------------------------------

const PricingBreakdown: React.FC = () => {
  const { token } = useAuth();
  return (
    <section id="pricing" style={{ marginTop: 96 }}>
      <SectionLabel left="Access" right="two tiers, one honest boundary" />
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24,
        marginTop: 32,
      }} className="n2-pricing">
        <PricingCard
          tier="Notches Weekly"
          price="Free"
          priceNote="No card required"
          included={[
            'One research letter every Friday',
            'This week\'s top reading, explained plainly',
            'Public Scorecard access — every notch, every outcome',
            'Methodology page — how the models work',
          ]}
          cta={
            token
              ? <SecondaryButton to="/profile">Manage in Profile</SecondaryButton>
              : <SecondaryButton to="/register?source=notches-weekly">Subscribe free</SecondaryButton>
          }
        />
        <PricingCard
          tier="Notches Research"
          price="$149"
          priceNote="per month · billed monthly"
          highlighted
          included={[
            'Everything in Notches Weekly',
            'Full letter archive — every issue, back to launch',
            'Deep-dive methodology walkthroughs per model',
            'Full audit trail — regime tags, model version, run reconstruction',
            'Direct email access to the desk for methodology questions',
          ]}
          cta={
            token
              ? <PrimaryButton to="/upgrade">Upgrade to Research</PrimaryButton>
              : <PrimaryButton to="/register?source=notches-research">Access the research</PrimaryButton>
          }
        />
      </div>

      <div style={{
        marginTop: 40, padding: '24px 28px',
        background: palette.paper, border: `1px solid ${palette.line}`,
        borderRadius: 3,
      }}>
        <div style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase',
          color: palette.goldDeep, marginBottom: 10,
        }}>Notches Managed · professional-only</div>
        <p style={{
          fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.65,
          color: palette.ink2, margin: 0, maxWidth: '68ch',
        }}>
          For RIAs and books over $200K, the WFS options auto-trader is
          available as a managed service — hosted in the WFS account or deployed
          in your own AWS account, with support and maintenance included. Custom
          contracts, direct desk support, never self-serve.{' '}
          <a href="/n2/about#managed" style={{
            color: palette.goldDeep, textDecoration: 'none',
            borderBottom: `1px solid ${palette.gold}60`,
          }}>See Notches Managed →</a>
        </p>
      </div>
    </section>
  );
};

const PricingCard: React.FC<{
  tier: string; price: string; priceNote: string;
  included: string[]; cta: React.ReactNode; highlighted?: boolean;
}> = ({ tier, price, priceNote, included, cta, highlighted }) => (
  <div style={{
    padding: '32px 32px 28px', borderRadius: 4,
    background: highlighted ? palette.paper2 : palette.paper,
    border: `1px solid ${highlighted ? palette.goldDeep : palette.line}`,
    borderWidth: highlighted ? '1.5px' : '1px',
    position: 'relative',
  }}>
    {highlighted && (
      <div style={{
        position: 'absolute', top: -1, left: 32, width: 40, height: 3,
        background: palette.gold,
      }} />
    )}
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
    }}>{tier}</div>
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 10,
    }}>
      <span style={{
        fontFamily: 'var(--serif)', fontSize: 44, color: palette.ink,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
        lineHeight: 1,
      }}>{price}</span>
      <span style={{
        fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      }}>{priceNote}</span>
    </div>
    <ul style={{
      listStyle: 'none', padding: 0, margin: '24px 0 24px',
    }}>
      {included.map((item, i) => (
        <li key={i} style={{
          fontFamily: 'var(--sans)', fontSize: 14, lineHeight: 1.55,
          color: palette.ink2, padding: '8px 0 8px 20px',
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute', left: 0, top: 12,
            width: 8, height: 1, background: palette.goldDeep,
          }} />
          {item}
        </li>
      ))}
    </ul>
    {cta}
  </div>
);

export default N2ResearchPage;
